import { Subject, Subscription } from 'rxjs'
import { Injector } from '@angular/core'
import { Store } from '@ngrx/store'
import { NavController, ViewController, MenuController, Platform } from 'ionic-angular'
import { ViewModel } from './view-model'
import { UIEventHub } from './services/event.service'
import { OverlayContainer } from '@angular/cdk/overlay'
import { SessionStorageUtil, AuthorizationService, ThemeService, IThemeInfo, AutoReloadService } from '@ar2/common'
import { AuthBasePage as Ar2BasePage } from '@ar2/common';;;;
    
//==========多言語化対応追加import=========
import { TranslateService } from '@ngx-translate/core';
import { UserSettingsService, ICultureInfo, CultureService, TimerUtil} from '@ar2/common';
import { ENV } from './config/config.dev'

/**
 * ページコンポーネントの共通基底クラス
 * @author nabla2.metamodel.generator
 */
export abstract class BasePage extends Ar2BasePage {

  /** 監視中のリアクティブストリームを一括解除するためのサブジェクト */
  protected unload$:Subject<void>

  /** 表示モデル */
  viewModel:ViewModel

  /** テーマサブスクリプション  */
  private themeSubScription: Subscription

  /** タイマー */
  private timeUtil: TimerUtil

  /** 画面コントローラー */
  protected navCtrl: NavController

  /** イベントハブ */
  protected eventHub: UIEventHub

  /** ビューコントローラー */
  public viewCtrl: ViewController

  /** メニューコントローラー */
  public menuCtrl: MenuController

  /** テーマサービス */
  public themeService: ThemeService

  /** プラットフォーム */
  public platform:Platform

  /** オーバーレイコンテナ */
  public overlayContainer:OverlayContainer

  //==========多言語化対応DI=========
  /** 翻訳サービス */
  private translate: TranslateService

  /** ユーザ設定 */
  private userSettings:UserSettingsService

  /** カルチャー */
  private culture: CultureService

  /**
   * コンストラクタ
   * @param injector Injector
   * @param store Reduxストア
   */
  constructor(
    protected injector: Injector,
    protected store: Store<{viewModel:ViewModel}>
  ) {
    super(injector)
    this.initializes();
    this.completeGettingAuthInfo.subscribe(e => {
      const authService = this.injector.get(AuthorizationService);
      const authUserInfo = JSON.parse(SessionStorageUtil.GET(AuthorizationService.AUTH_USER_INFO));
      const selectedGbuCd = authService.getSelectedGbuCD();
      const options = [];
      let hasSelectedGbuCd = false;
      if (authUserInfo) {
        authUserInfo.forEach(userInfo => {
          options.push({
            label: userInfo.gbuName,
            value: userInfo.gbuCd
          });
          if (userInfo.gbuCd === selectedGbuCd) {
            hasSelectedGbuCd = true;
          }
        });
      }
      this.viewModel = ViewModel.update(
        'cmnfnc_base.user.user_role_pulldown_list',
        options,
        this.viewModel
      );
      this.viewModel = ViewModel.update(
        'cmnfnc_base.user_role_selected',
        hasSelectedGbuCd ? selectedGbuCd : (selectedGbuCd && options[0] ? options[0].value : ''),
        this.viewModel
      );
    });
  }

  /**
   * リアクティブストリームの監視を終了する。
   */
  ionViewDidLeave():void {
    this.unload$.next()
    this.unload$.complete()
  }

  /**
   * モーダル閉じる処理定義
   */
  closeModal() {
    this.viewCtrl.dismiss();
  }
  /**
   * Initializes this page.
   */
  private initializes(): void {
    this.culture = this.injector.get(CultureService);
    this.eventHub = this.injector.get(UIEventHub);
    this.menuCtrl = this.injector.get(MenuController);
    this.navCtrl = this.injector.get(NavController);
    this.overlayContainer = this.injector.get(OverlayContainer);
    this.platform = this.injector.get(Platform);
    this.themeService = this.injector.get(ThemeService);
    this.timeUtil = this.injector.get(TimerUtil)
    this.translate = this.injector.get(TranslateService);
    this.userSettings = this.injector.get(UserSettingsService);
    this.viewCtrl = this.injector.get(ViewController);
    const autoReloadService = this.injector.get(AutoReloadService);
    autoReloadService.checkAppVersion(true)
    this.autoReloadPolling = this.injector.get(TimerUtil)
      .interval(Ar2BasePage.AUTO_RELOAD_POLLING_MINUTES * 1000 * 60)
      .subscribe(() => {
        autoReloadService.checkAppVersion(true);
      });
    this.platform.ready().then(() => {
      this.initializeTranslate()
      this.initializeTheme()
      this.loadUserSettings()
    });
    if (this.menuCtrl.isOpen()) {
      this.menuCtrl.close();
    }
  }

  /**
   * Initializes the theme.
   */
  private initializeTheme(): void {
    const self = this;
    this.themeSubScription = this.themeService.onThemeChange.subscribe(_ => {
      const parentEl = this.overlayContainer.getContainerElement()
        .parentElement;
      if (parentEl.className) {
        parentEl.classList.remove(parentEl.className)
      }
      parentEl.classList.add(self.themeService.currentThemeInfo.cssClass)
    });
    this.onChangeTheme(this.themeService.currentThemeInfo.cssClass)
  }

  /**
   * Gets the current theme.
   */
  public get currentTheme(): any {
    return this.themeService.currentThemeInfo
  }

  /**
   * Gets list of the support theme
   */
  public get supportThemes(): IThemeInfo[] {
    return this.themeService.supportTheme()
  }

  /**
   * Event handler for Change the theme
   */
  public onChangeTheme(selectedItem: any): void {
    this.themeService.changeTheme(selectedItem.value)
  }

  //==========多言語化対応=========
  /**
   *
   *
   * @memberof MainMenuPage
   */
  public getCurrentLanguageCode = false;

  /**
   * Gets the current language.
   *
   * @readonly
   * @type {any}
   * @memberof AppPage
   */
  public get currentLanguage(): any {
    const code = this.translate.currentLang;
    if (code) {
      this.getCurrentLanguageCode = true;
      return ENV.getCulture(code);
    }
    this.timeUtil.timeout(0).subscribe(() => {
      const codes = this.currentLanguage;
      console.log(codes);
    });
    return {
      code: []
    };
  }

  /**
   * Initializes the language for the translate service
   *
   * @private
   * @memberof AppPage
   */
  private initializeTranslate(): void {
    const langs = ENV.getCultureCodes();
    this.translate.addLangs(langs);

    const defaultIndex = langs.indexOf('en-US');
    if (defaultIndex > -1) {
      this.translate.setDefaultLang('en-US');
      this.translate.use('en-US');
    } else {
      this.translate.setDefaultLang(langs[0]);
      this.translate.use(langs[0]);
    }
  }

  /**
   * Load the user settings from local storage
   *
   * @private
   * @memberof AppPage
   */
  private loadUserSettings(): void {
    const langs = ENV.getCultureCodes();

    const userSettings = this.userSettings.getSettings();
    if (userSettings) {
      if (userSettings.language) {
        const langIndex = langs.indexOf(userSettings.language);
        if (langIndex > -1) {
          this.translate.use(langs[langIndex]);
        }
      }
    }
  }

  /**
   * Event handler for Change the language
   *
   * @param {*} selectedItem selected item for language
   * @memberof AppPage
   */
  public onChangeLang(selectedItem: any): void {
    const settings = this.userSettings.getSettings();
    settings.language = selectedItem.value;

    this.translate.use(settings.language);
    this.userSettings.saveSettings(settings);
  }

  /**
   * Gets list of the support country
   *
   * @readonly
   * @type {ICultureInfo[]}
   * @memberof AppPage
   */
  public get supportCultures(): ICultureInfo[] {
    return this.culture.supportCultures();
  }

  /**
   * Gets the current culture.
   *
   * @readonly
   * @type {any} country code
   * @memberof AppPage
   */
  public get currentCulture(): any {
    return this.culture.currentCultureInfo;
  }

  /**
   * Event handler for Change the culture
   *
   * @param {*} selectedItem selected item for culture
   * @memberof AppPage
   */
  public onChangeCulture(selectedItem: any): void {
    this.culture.changeCulture(selectedItem.value);
  }
}


// WEBPACK FOOTER //
// ./src/base-page.ts